import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";
import {
  useSEOMeta,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQPageSchema,
} from "@/components/SEO";

const otherAreas = [
  { title: "Health", href: "/our-work/health", icon: "ri-heart-pulse-line", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { title: "Economic Security", href: "/our-work/economic-security", icon: "ri-seedling-line", color: "bg-teal-50 text-teal-700 border-teal-200" },
  { title: "Protection", href: "/our-work/protection", icon: "ri-shield-check-line", color: "bg-rose-50 text-rose-700 border-rose-200" },
];

export default function EducationPage() {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useSEOMeta({
    title: "Education - BHODEV Cameroon | Quality Education for Displaced Children & Youth",
    description:
      "BHODEV ensures displaced children and youth in Cameroon enjoy high-quality, safe and inclusive learning opportunities. Back-to-school scholarships, non-formal education, and vocational training across North West and South West regions.",
    keywords:
      "BHODEV education, Cameroon education NGO, back to school Cameroon, displaced children education, youth education Cameroon, vocational training Cameroon, INEE standards, SDG 4 Cameroon, non-formal education",
    canonicalUrl: "https://bhodevcameroon.org/our-work/education",
    ogImage:
      "https://readdy.ai/api/search-image?query=children%20and%20youth%20in%20a%20classroom%20in%20Cameroon%20learning%20with%20teacher%20bright%20educational%20environment%20hopeful%20atmosphere%20warm%20tones%20documentary%20photography%20wide%20landscape&width=1200&height=630&seq=education-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Our Work", url: "https://bhodevcameroon.org/our-work" },
        { name: "Education", url: "https://bhodevcameroon.org/our-work/education" },
      ]),
      ...generateWebPageSchema(
        "Education - BHODEV Cameroon",
        "BHODEV ensures displaced children and youth in Cameroon enjoy high-quality, safe and inclusive learning opportunities through scholarships, non-formal education, and vocational training.",
        "https://bhodevcameroon.org/our-work/education"
      ),
      ...generateFAQPageSchema([
        {
          question: "What education programs does BHODEV offer?",
          answer:
            "BHODEV offers back-to-school scholarships, non-formal education opportunities, transition support to formal education, youth education and wellbeing programs, and ICT training for youth across Cameroon's crisis-affected regions.",
        },
        {
          question: "How does BHODEV support displaced children's education?",
          answer:
            "BHODEV provides back-to-school scholarships, donates learning materials, pays school fees, and supports the transition and inclusion of internally displaced children into formal education systems in Cameroon's North West and South West regions.",
        },
      ]),
    },
  });

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("sector", "Education")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching education projects:", error);
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fallbackProjects = [
    {
      title: "Back-to-School Scholarship Programme",
      category: "Access to Education",
      description: "Providing back-to-school scholarships, donation of learning materials and payment of school fees for vulnerable children and youth in crisis-affected regions.",
      year: "2023",
      image: "https://readdy.ai/api/search-image?query=children%20receiving%20school%20supplies%20and%20scholarships%20in%20Cameroon%20back%20to%20school%20programme%20happy%20students%20with%20backpacks%20bright%20educational%20setting&width=400&height=260&seq=ep1&orientation=landscape",
    },
    {
      title: "Non-Formal Education Opportunities",
      category: "Non-Formal Education",
      description: "Providing accredited non-formal education opportunities relevant to children's individual learning needs, including technical and vocational education.",
      year: "2023",
      image: "https://readdy.ai/api/search-image?query=vocational%20training%20for%20youth%20in%20Cameroon%20non%20formal%20education%20skills%20development%20workshop%20young%20people%20learning%20practical%20skills%20warm%20light&width=400&height=260&seq=ep2&orientation=landscape",
    },
    {
      title: "ICT Training for Youth",
      category: "Youth Education",
      description: "Providing youths with opportunities for post-primary education and ICT training to enhance their digital skills and livelihood opportunities.",
      year: "2022",
      image: "https://readdy.ai/api/search-image?query=youth%20ICT%20training%20in%20Cameroon%20young%20people%20learning%20computer%20skills%20in%20a%20classroom%20technology%20education%20hopeful%20atmosphere&width=400&height=260&seq=ep3&orientation=landscape",
    },
    {
      title: "Teacher Development Programme",
      category: "Professional Development",
      description: "Supporting curriculum development for children and professional teacher development because professional teacher development is key to achieving quality education.",
      year: "2022",
      image: "https://readdy.ai/api/search-image?query=teacher%20training%20workshop%20in%20Cameroon%20professional%20development%20for%20educators%20classroom%20setting%20collaborative%20learning%20warm%20natural%20light&width=400&height=260&seq=ep4&orientation=landscape",
    },
  ];

  const displayProjects = projects.length > 0 ? projects : fallbackProjects;

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[460px] md:h-[520px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frenovation-and-reconstruction-of-bomaka-primary-school-buildings%2FIMG-20231209-WA0103.jpg?alt=media&token=1c3a574b-d92a-480d-9e14-ce603ff2c878"
            alt="BHODEV Education Work in Cameroon - Scholarships and Learning"
            title="BHODEV Education Work in Cameroon - Scholarships and Learning"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto w-full">
          <nav className="flex items-center justify-center gap-2 text-xs text-white/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors duration-200">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/our-work" className="hover:text-white transition-colors duration-200">Our Work</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/90">Education</span>
          </nav>
          <span className="inline-block bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wider uppercase">Core Sector</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Education</h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            Education is a fundamental human right for all children and youth as stipulated by the United Nations Charter of Human Right (UNCHR).
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-amber-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="w-full lg:w-3/5">
              <span className="text-xs font-semibold uppercase tracking-widest text-amber-700 mb-3 block">Core Sectors — Education</span>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-5">Our Approach to Education</h2>
              <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-4">
                Quality education provides children and youths with the skills and confidence they need to live a life of value. Education generates the voice through which other rights can be heard. <strong>BHODEV firmly believes that education empowers and helps people reach their full potential.</strong>
              </p>
              <p className="text-stone-700 text-sm md:text-base leading-relaxed">
                Humanitarian crisis has caused a lot of displacement that has led to a devastating impact on learning leading to educational disruption and closure. It has caused many children and youth to suffer from traumatic experiences and a loss of the social networks that provide protection and support.
              </p>
            </div>
            <div className="w-full lg:w-2/5 grid grid-cols-2 gap-3">
              {[
                { icon: "ri-graduation-cap-line", label: "Response Areas", value: "4" },
                { icon: "ri-user-heart-line", label: "Youth Supported", value: "5K+" },
                { icon: "ri-book-2-line", label: "INEE Standards", value: "Aligned" },
                { icon: "ri-global-line", label: "SDG Aligned", value: "SDG 4" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl p-4 border border-amber-100 text-center">
                  <div className="w-8 h-8 flex items-center justify-center text-amber-600 mx-auto mb-2">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="text-lg font-bold text-stone-800">{stat.value}</p>
                  <p className="text-xs text-stone-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <h3 className="text-lg font-bold text-stone-800 mb-4 uppercase tracking-wide">Our Expertise in Education</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Naturally, the capacity of educational bodies to deliver good quality education is significantly reduced during crisis period due to the displacement of many children and young people. As such, the host communities often find it difficult to integrate the displaced children and young people into the existing educational systems.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                <strong>BHODEV's aim is to ensure that all displaced children and youths</strong> (boys and girls, refugees, returnees, IDPs and vulnerable host communities) affected by the Socio-Political Crisis in Cameroon enjoy high-quality, safe and inclusive learning opportunities relevant to their psychosocial, emotional and cognitive development and needs.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mb-5">
                With respect to Sustainable Development Goal 5, BHODEV works to promote quality education for vulnerable groups, especially women and children through back-to-school scholarship activities, donation of learning materials and payment of school fees. BHODEV also promote and support the transition and inclusion of all the internally displaced children and youth into formal education systems, so they can benefit from an accredited education. We ensure to do this by providing opportunities for school-aged children (between 4 and 18 years old) and youth (between 15 and 25 years old) in the Crisis affected regions of Cameroon to exercise their right to education and enhance their wellbeing. Our major focus is on the children who are out of school or have had their education interrupted. We support them by providing opportunities for completing a full cycle of education for effective learning outcomes.
              </p>

              <h4 className="text-sm font-bold text-stone-700 mb-3">Our education activities primarily focus on four response areas:</h4>
              <ul className="flex flex-col gap-2">
                {[
                  "First-Line Education Response",
                  "Non-Formal Education Opportunities",
                  "Transition to and Retention in Formal Education",
                  "Youth Education and Wellbeing",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-stone-600">
                    <div className="w-4 h-4 flex items-center justify-center text-amber-600 shrink-0">
                      <i className="ri-checkbox-circle-line text-base"></i>
                    </div>
                    <strong>{item}</strong>
                  </li>
                ))}
              </ul>
              <p className="text-stone-500 text-xs mt-4 italic">All our educational programmes are designed following the Minimum Standard for Education by the Inter-Agency Network for Education in Emergencies (INEE).</p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-stone-800 mb-4 uppercase tracking-wide">Safe Learning Environments</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                We work in collaboration with other educational institutions to ensure a safe learning environment for women and children through sensitisation, orientation on the importance of allowing girls to make educational choices free of gender stereotypes, promotion of adult learning and literacy through ICT training. We provide youths with opportunities for post-primary education, accredited non-formal education opportunities relevant to children's individual learning needs, including technical and vocational education, supporting them to engage meaningfully in their communities and access livelihood opportunities as not all children and youth are able to participate in formal education.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed">
                Although recognising that the government is the primary duty-bearer, <strong>BHODEV stands to support them to uphold their duties</strong> by assisting in carrying out curriculum development for children and professional teacher development because professional teacher development is key to achieving quality education through the provision of continuous training and follow-up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-2">Organization Projects under Education</h2>
          <p className="text-stone-500 text-sm mb-10">Programmes and initiatives driving educational outcomes in Cameroon</p>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-amber-600 text-2xl"></i>
            </div>
          )}

          {!loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayProjects.map((project) => (
                <Link
                  key={project.id || project.title}
                  to={project.id ? `/projects/${project.id}` : "#"}
                  className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-200 transition-all duration-300 group cursor-pointer block"
                >
                  <div className="w-full h-[160px]">
                    <img
                      src={project.image_url || project.image}
                      alt={project.title}
                      title={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                      {project.status || project.category}
                    </span>
                    <h3 className="text-sm font-bold text-stone-800 mb-2 leading-snug">{project.title}</h3>
                    <p className="text-xs text-stone-500 leading-relaxed line-clamp-3">
                      {project.description || project.excerpt || ""}
                    </p>
                    <p className="text-xs text-stone-400 mt-3 font-medium">{project.start_date || project.year}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Other Core Areas */}
      <section className="py-12 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-stone-800 mb-6">Explore Other Core Areas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {otherAreas.map((area) => (
              <Link
                key={area.title}
                to={area.href}
                className={`flex items-center gap-3 px-5 py-4 rounded-xl border ${area.color} hover:scale-105 transition-all duration-200 cursor-pointer`}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className={`${area.icon} text-xl`}></i>
                </div>
                <span className="font-semibold text-sm">{area.title}</span>
                <i className="ri-arrow-right-line ml-auto text-sm"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}